import React from 'react';
import {
  ExternalLink,
  LineChart,
  Pause,
  Play,
  Trash2,
} from 'lucide-react';
import type { WatchlistViewItem } from './types';
import { formatCurrencyPrice } from '../../utils/currency';
import { formatRelativeTime } from '../../utils/dates';
import { StatusBadge } from '../../components/common';

export interface WatchlistTableProps {
  watches: WatchlistViewItem[];
  onTogglePause: (watch: WatchlistViewItem) => void;
  onDelete: (watch: WatchlistViewItem) => void;
  onViewHistory: (watch: WatchlistViewItem) => void;
}

export const WatchlistTable: React.FC<WatchlistTableProps> = ({
  watches,
  onTogglePause,
  onDelete,
  onViewHistory,
}) => {
  return (
    <div className="w-full overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
      <table className="w-full text-left border-collapse text-xs">
        <thead>
          <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50/75 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-semibold uppercase tracking-wider">
            <th className="py-3 px-4">Product</th>
            <th className="py-3 px-4">Store</th>
            <th className="py-3 px-4">Current Price</th>
            <th className="py-3 px-4">Status</th>
            <th className="py-3 px-4">Last Check</th>
            <th className="py-3 px-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {watches.map((watch) => {
            const formattedPrice = formatCurrencyPrice(
              watch.currentPrice,
              watch.establishedCurrency
            );
            const formattedListPrice = watch.listPrice
              ? formatCurrencyPrice(watch.listPrice, watch.establishedCurrency)
              : null;

            return (
              <tr
                key={watch.id}
                onClick={() => onViewHistory(watch)}
                className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40 transition-colors cursor-pointer"
              >
                {/* Product Title */}
                <td className="py-3 px-4 max-w-sm">
                  <div className="font-medium text-slate-900 dark:text-slate-100 line-clamp-1">
                    {watch.productTitle}
                  </div>
                  {watch.alertRule?.targetPrice && (
                    <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                      Target: {formatCurrencyPrice(watch.alertRule.targetPrice, watch.establishedCurrency)}
                    </span>
                  )}
                </td>

                {/* Store */}
                <td className="py-3 px-4 text-slate-600 dark:text-slate-300 font-medium">
                  <div className="flex items-center gap-1.5">
                    <span>{watch.merchantName || watch.merchantDomain}</span>
                    {watch.storefrontContext?.country_code && (
                      <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded border border-slate-200 dark:border-slate-700">
                        {watch.storefrontContext.country_code}
                      </span>
                    )}
                  </div>
                </td>

                {/* Price */}
                <td className="py-3 px-4">
                  <div className="flex items-baseline gap-1.5 font-bold text-slate-900 dark:text-slate-100">
                    <span>{formattedPrice}</span>
                    {formattedListPrice && (
                      <span className="text-[10px] text-slate-400 line-through font-normal">
                        {formattedListPrice}
                      </span>
                    )}
                  </div>
                </td>

                {/* Status Badge */}
                <td className="py-3 px-4">
                  {watch.trackabilityStatus === 'GEO_MISMATCH' ? (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800/60"
                      title={watch.lastFailureCode ? `Held: ${watch.lastFailureCode}` : 'Held for review'}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                      <span>HELD</span>
                    </span>
                  ) : (
                    <StatusBadge status={watch.isActive ? 'ACTIVE' : 'PAUSED'} />
                  )}
                </td>

                {/* Last Check */}
                <td className="py-3 px-4 text-slate-400">
                  {formatRelativeTime(watch.lastSuccessAt)}
                </td>

                {/* Actions */}
                <td className="py-3 px-4 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      onClick={() => onTogglePause(watch)}
                      className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
                      title={watch.isActive ? 'Pause' : 'Resume'}
                    >
                      {watch.isActive ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                    </button>

                    <button
                      onClick={() => onViewHistory(watch)}
                      className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-emerald-600"
                      title="View Price History"
                    >
                      <LineChart className="w-3.5 h-3.5" />
                    </button>

                    <a
                      href={watch.requestedUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
                      title="Open store"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>

                    <button
                      onClick={() => onDelete(watch)}
                      className="p-1 rounded hover:bg-rose-50 dark:hover:bg-rose-950/40 text-slate-400 hover:text-rose-600"
                      title="Delete watch"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
